/**
 * Two-way binding between the `/…/kbs/:kbId/f/:fileId` URL and the file open in
 * the `AuiFiles` preview pane.
 *
 * - **URL → preview:** resolves `:fileId` to a path (`useKbNodePath`) and hands
 *   `previewPath` / `previewKey` to `AuiFiles`, which reveals + opens the file
 *   (expanding + lazy-loading its folder as needed) on load or reload.
 * - **preview → URL:** `onPreviewChange` pushes the selection back into the URL
 *   with `replace` (no history spam). A loop guard skips the write when the URL
 *   already points at that file (i.e. the change originated from the URL).
 *
 * The three KB surfaces (operator, space console, org admin) share this by
 * passing their own route builders — that's their only difference.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui';
import { useKbNodePath } from './useKbNodePath';
import type { VfsEntry } from '@/types/vfs';

interface UseKbDeepLinkArgs {
  kbId: string | null | undefined;
  /** The `:fileId` from the URL (undefined on the KB-root route). */
  fileId: string | null | undefined;
  /** App-relative deep link to a file in this surface's route family. */
  fileUrl: (fileId: string) => string;
  /** App-relative URL of the KB root in this surface (preview closed). */
  rootUrl: string;
}

interface UseKbDeepLinkResult {
  previewPath: string | undefined;
  previewKey: string | undefined;
  onPreviewChange: (entry: VfsEntry | null) => void;
  buildShareUrl: (entry: VfsEntry) => string;
}

export function useKbDeepLink({
  kbId,
  fileId,
  fileUrl,
  rootUrl,
}: UseKbDeepLinkArgs): UseKbDeepLinkResult {
  const navigate = useNavigate();
  const toast = useToast();
  const { path, error } = useKbNodePath(kbId, fileId);

  // Unresolvable deep link: the resolver collapses every "not here" case
  // (unknown / moved-out / deleted / no access / cross-KB) to a 404, which is
  // a normal outcome for a shared or bookmarked short-id link — not an error.
  // Note it once (info, not a red error toast) and leave the user on the KB
  // browser. We deliberately do NOT redirect: that would also fire if an
  // actively-open file's id momentarily failed to resolve, yanking the user
  // out of their current view.
  const notedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!fileId || !error) return;
    if (notedForRef.current === fileId) return;
    notedForRef.current = fileId;
    toast.info('That file is no longer here — it may have been moved or deleted.');
  }, [fileId, error, toast]);

  const onPreviewChange = (entry: VfsEntry | null) => {
    const targetFileId = entry?.fileId ?? null;
    // Loop guard: the URL already reflects this file (the open came from the
    // URL) — don't re-navigate to the same place.
    if (targetFileId === (fileId ?? null)) return;
    navigate(entry ? fileUrl(entry.fileId) : rootUrl, { replace: true });
  };

  return {
    previewPath: path ?? undefined,
    previewKey: fileId ?? undefined,
    onPreviewChange,
    buildShareUrl: (entry) => fileUrl(entry.fileId),
  };
}
